import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { Field, Item, RichText, Text, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps } from 'lib/ComponentProps';

import { toKebabCase } from 'src/utils/helpers';
import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import ChevronDownIcon from '../../../../public/icons/ChevronDown.svg';

import styles from './FAQListing.module.css';

type FAQListingProps = ComponentProps & {
	fields: {
		Title: Field<string>;
		items: Item &
			[
				{
					fields: {
						Title: Field<string>;
						Information: Field<string>;
					};
				}
			];
	};
};

export default withDatasourceCheck()<FAQListingProps>(FAQListing);

function FAQListing(props: FAQListingProps): JSX.Element {
	const isEditing = IsSitecoreEditing();

	const router = useRouter();
	const hash = router.asPath.split('#')[1] || '';

	const questionRefs = useRef<Array<HTMLHeadingElement | null>>([]);
	const answerRefs = useRef<Array<HTMLDivElement | null>>([]);

	function toggleCollapsible(index: number) {
		const answerEl = answerRefs.current[index];
		const questionEl = questionRefs.current[index];

		if (!answerEl || !questionEl) {
			return;
		}

		if (answerEl.offsetHeight === 0) {
			answerEl.style.height = `${answerEl.scrollHeight}px`;

			questionEl.classList.add('open');
		} else {
			answerEl.style.height = '0px';

			questionEl.classList.remove('open');
		}
	}

	useEffect(() => {
		const questions = props.fields?.items.map((faq) => {
			return toKebabCase(faq.fields.Title?.value);
		});

		const bookmarkedAnswerEl = answerRefs.current[questions?.indexOf(hash.toLowerCase())];

		if (bookmarkedAnswerEl) {
			bookmarkedAnswerEl.style.height = `${bookmarkedAnswerEl.scrollHeight}px`;
		}
	}, []);

	return (
		<section className={styles.section + ' pad-x maxWidth'}>
			<div className={styles.container}>
				<Text field={props.fields?.Title} tag="h2" className={styles.title} />

				{props.fields?.items && (
					<div className={styles.faqColumn}>
						{props.fields?.items.map((faq, index) => (
							<div className={`${styles.faq} greyLight rounded`} key={index}>
								<h3
									id={toKebabCase(faq.fields.Title?.value)}
									className={styles.question}
									ref={(el: HTMLDivElement) => (questionRefs.current[index] = el)}
									onClick={() => (!isEditing ? toggleCollapsible(index) : undefined)}
								>
									<Text field={faq.fields.Title} tag="span" />

									<ChevronDownIcon className={styles.icon} />
								</h3>
								<div
									className={styles.answer}
									style={!isEditing ? { height: 0 } : { height: 'auto' }}
									ref={(el: HTMLDivElement) => (answerRefs.current[index] = el)}
								>
									<RichText field={faq.fields.Information} className={styles.richtext} tag="div" />
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</section>
	);
}
